<!-- 
IMPORTANTE: NO AGREGAR LÓGICA DE NEGOCIO EN ESTE COMPONENTE.
SOLO MUESTRA LOS DATOS QUE RECIBE POR PROPS.
TODA LA LÓGICA DE NEGOCIO DEBE ESTAR EN EL STORE O EN EL COMPONENTE PADRE.
-->
<template>
    <div class="summary-card">
        <div class="summary-card-header">
            <div class="summary-card-title">
                <i v-if="icon" :class="icon" class="i-andy-m"></i>
                <h3 v-if="titleKey" class="summary-card-title-text">{{ $t(titleKey) }}</h3>
            </div>
            <!-- red tag with badgeCount and badgeLabel and collapse button for mobile -->
            <div class="summary-card-badge-container">
                <div v-if="showBadge" class="summary-card-badge">
                    <span v-if="showBadgeDot" class="summary-card-badge-dot"></span>
                    <span v-if="badgeCount" class="summary-card-badge-count">{{ badgeCount }}</span>
                    <span v-if="badgeLabel" class="summary-card-badge-label">{{ getBadgeLabel(badgeCount) }}</span>
                </div>
                <div class="colapse-button-container" v-if="isMobileView">
                    <button class="colapse-button" @click="toggleCollapse">
                        <i class="i-andy-chevron-down i-andy-s" v-if="isCollapsed"></i>
                        <i class="i-andy-chevron-up i-andy-s" v-else></i>
                    </button>
                </div>
            </div>
        </div>
        <div class="summary-card-content" v-show="!isCollapsed">
            <div class="summary-card-items">
                <button v-for="(item, index) in items" :key="index" class="summary-card-item" @click="handleItemClick(item)">
                    <div v-if="item.icon" class="summary-card-item-icon" :class="item.iconColor">
                        <i :class="item.icon" class="i-andy-m"></i>
                    </div>
                    <span v-if="item.labelKey" class="summary-card-item-label">{{ $t(item.labelKey) }}</span>
                    <span v-if="item.count" class="summary-card-item-value">
                        <strong>{{ item.count }}</strong>
                        <span v-if="item.total !== undefined && item.total !== null" class="summary-card-item-total">/{{ item.total }}</span>
                    </span>
                    <div v-if="item.route" class="summary-card-item-link">
                        <i class="i-andy-external"></i>
                    </div>
                </button>
            </div>
            <div v-if="lastSyncedDate && !isCollapsed" class="summary-card-last-synced-at-container">
                <i class="i-andy-s i-andy-history summary-card-last-synced-at-icon"></i>
                <span class="summary-card-last-synced-at-text">{{ $t('radar.last_synced_at', { date: lastSyncedDate, time: lastSyncedTime }) }}</span>
            </div>
        </div>
    </div>
</template>

<script>
const MOBILE_MAX_WIDTH = 767

const resolveIsMobileView = () => {
    return typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH
}

export default {
    name: 'SummaryCard',
    props: {
        icon: {
            type: String,
            default: ''
        },
        titleKey: {
            type: String,
            required: true
        },
        showBadge: {
            type: Boolean,
            default: false
        },
        badgeCount: {
            type: [Number, String],
            default: null
        },
        badgeLabel: {
            type: String,
            default: ''
        },
        showBadgeDot: {
            type: Boolean,
            default: true
        },
        items: {
            type: Array,
            default: () => []
        },
        toolCode: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            isCollapsed: false,
            isMobileView: resolveIsMobileView()
        }
    },
    computed: {
        lastSyncedTimestamp() {
            return this.$store.getters['radar/getToolLastSyncedAt'](this.toolCode)
        },
        lastSyncedDate() {
            if (!this.lastSyncedTimestamp) {
                return ''
            }

            return moment(this.lastSyncedTimestamp).format('D MMM').replace(/\.$/, '')
        },
        lastSyncedTime() {
            if (!this.lastSyncedTimestamp) {
                return ''
            }

            return moment(this.lastSyncedTimestamp).format('HH:mm')
        }
    },
    methods: {
        updateIsMobileView() {
            this.isMobileView = window.innerWidth <= MOBILE_MAX_WIDTH

            if (!this.isMobileView) {
                this.isCollapsed = false
            }
        },
        toggleCollapse() {
            this.isCollapsed = !this.isCollapsed
        },
        handleItemClick(item) {
            this.$emit('item-click', item)
        },
        getBadgeLabel(badgeCount) {
            const count = Number(badgeCount)

            if (!count || count < 1) {
                return ''
            }

            return this.$tc(this.badgeLabel, count)
        }
    },
    created() {
        window.addEventListener('resize', this.updateIsMobileView)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateIsMobileView)
    }
}
</script>

<style lang="scss" scoped>
.summary-card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    padding: 16px;
    background: $color-white;
    border-radius: 4px;
    width: 100%;

    @media (min-width: 1440px) {
        min-width: 425.33px;
        max-width: 425.33px;
    }

    h3 {
        padding: 0;
    }

    .summary-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;

        .summary-card-title {
            display: flex;
            align-items: center;
            gap: 8px;

            i {
                font-size: 22px;
                color: var(--neutro-s-60-app, #5b5c5f);
            }

            h3 {
                margin: 0;
                font-family: $text-bold;
                font-size: 20px;
                color: var(--neutro-s-60-app, #5b5c5f);
            }
        }

        .summary-card-badge-container {
            display: flex;
            align-items: center;
            gap: 8px;

            .colapse-button {
                width: 32px;
                height: 32px;
                border: none;
                background: transparent;
                cursor: pointer;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                background: $neutro-t70;

                i {
                    font-size: 18px;
                    color: $color-black;
                }
            }
        }

        .summary-card-badge {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 4px;
            border-radius: 4px;
            background: $color-error-100;
            white-space: nowrap;

            .summary-card-badge-dot {
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background: $color-error-500;
                flex-shrink: 0;
            }

            .summary-card-badge-count,
            .summary-card-badge-label {
                font-family: $text-bold;
                font-size: 14px;
                color: $color-error-500;
            }
        }
    }

    .summary-card-content {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .summary-card-items {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .summary-card-item {
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            border: 1px solid $color-neutral-300;
            border-radius: 4px;
            background: transparent;

            .summary-card-item-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                flex-shrink: 0;
                border-radius: 4px;
                background: $color-neutral-100;

                i {
                    font-size: 18px;
                    color: $color-neutral-600;
                }

                &.error {
                    background: $color-error-100;

                    i {
                        color: $color-error-500;
                    }
                }

                &.warning {
                    background: $color-warning-100;

                    i {
                        color: $color-warning-800;
                    }
                }

                &.neutral {
                    background: $color-neutral-100;

                    i {
                        color: $color-neutral-500;
                    }
                }
            }

            .summary-card-item-label {
                flex: 1;
                font-family: $text-medium;
                font-size: 16px;
                color: $color-black;
            }

            .summary-card-item-value {
                font-family: $text-bold;
                font-size: 24px;
                color: $color-black;
                white-space: nowrap;

                .summary-card-item-total {
                    font-family: $text-medium;
                    font-size: 16px;
                    color: $color-neutral-600;
                }
            }

            .summary-card-item-link {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 4px;
                border: none;
                background: transparent;
                flex-shrink: 0;

                i {
                    font-size: 18px;
                    color: $main-text;
                }
            }
        }

        .summary-card-item:hover {
            background: transparent;
            border-color: $main-light;
        }
    }

    // Ajuste solo para móvil (portrait): reduce el tamaño de título y etiquetas para
    // evitar que el botón de la cabecera se salga de la caja con títulos largos (idiomas
    // con textos extensos). El breakpoint coincide con el modo "portrait" de vue-mq
    // (breakpoints.portrait = 901 => max-width: 900px); landscape/desktop no se tocan.
    @media (max-width: 900px) {
        .summary-card-header {
            .summary-card-title h3 {
                font-size: 18px;
            }

            .summary-card-badge {
                .summary-card-badge-count,
                .summary-card-badge-label {
                    font-size: 12px;
                }
            }
        }
    }

    .summary-card-last-synced-at-container {
        display: flex;
        align-items: center;
        gap: 4px;

        .summary-card-last-synced-at-icon {
            color: $neutro-s30;
        }
        .summary-card-last-synced-at-text {
            font-size: 12px;
            color: $neutro-s30;
        }
    }
}
</style>
